import isEqual from 'lodash/isEqual';

import {
  ListItem,
  mergeSx,
  Select,
  SelectProps,
  SxProps,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { VulnerabilitySeverity } from '@cast/types';

import { availableSeverityLevels, severityToTextMap } from './constants';
import { SeverityChip } from './SeverityChip';

import {
  useFiltersDropdownAggregate,
  useSearchCriterion,
  makeEnumAggregateFn,
} from '@cast/shared-search-ui';

type Props = {
  criterion: string;
  showSeverityCount?: boolean;
  sx?: SxProps;
  analyticsHandler?: (value: any) => void;
  testId?: string;
  selectProps?: SelectProps<VulnerabilitySeverity, unknown>;
};

export const SeveritySelect = ({
  criterion,
  showSeverityCount,
  sx,
  analyticsHandler,
  testId,
  selectProps,
}: Props) => {
  const _criterion = useSearchCriterion(criterion);

  const { aggregate, dropdownProps } = useFiltersDropdownAggregate(
    criterion,
    makeEnumAggregateFn(VulnerabilitySeverity, 'severityLevel'),
    showSeverityCount
  );

  return (
    <Select
      testId={testId}
      size="medium"
      sx={mergeSx({ width: 133 }, sx)}
      inputText="Severity"
      checkable
      multiple
      value={_criterion.value}
      onChange={(option) => {
        if (option?.length) {
          _criterion.setValue?.(option);
          analyticsHandler?.(option);
        } else {
          _criterion.clear?.();
        }
      }}
      options={availableSeverityLevels}
      applySortingWhileSearching={false}
      renderOption={(level: VulnerabilitySeverity, itemProps) => (
        <ListItem {...itemProps}>
          <Stack direction="row">
            <SeverityChip sx={{ width: '100%' }} className={level}>
              {level in severityToTextMap ? severityToTextMap[level] : level}
            </SeverityChip>
            {showSeverityCount && (
              <Typography
                variant="P14R"
                color={tokens.theme.fg.structural.secondary}
                ml={8}
                width={41}
                textAlign="right"
              >
                {aggregate[level]}
              </Typography>
            )}
          </Stack>
        </ListItem>
      )}
      isOptionEqualToValue={isEqual}
      {...dropdownProps}
      {...(selectProps as any)}
    />
  );
};
